// ABOUTME: Displays an offline notice when the user loses internet connection
// ABOUTME: Provides appropriate information about app functionality while offline

import { useState, useEffect } from 'react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, WifiOff, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function OfflineNotice() {
  const [isOnline, setIsOnline] = useState(true);
  const [showAlert, setShowAlert] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    // Check initial online status
    setIsOnline(navigator.onLine);
    
    // Event handlers for online/offline status changes
    const handleOnline = () => {
      setIsOnline(true);
      
      // If we were previously offline, show the reconnected message
      if (!isOnline) {
        setWasOffline(true);
        // Hide the reconnected message after 5 seconds
        setTimeout(() => {
          setWasOffline(false);
        }, 5000);
      }
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      setShowAlert(true);
    };
    
    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Clean up event listeners on component unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isOnline]);

  // Handle manual refresh
  const handleRefresh = () => {
    window.location.reload();
  };

  // If online and alert isn't forced to show, don't render anything
  if (isOnline && !showAlert && !wasOffline) {
    return null;
  }

  // Show reconnected message
  if (isOnline && wasOffline) {
    return (
      <Alert className="fixed bottom-4 left-4 right-4 z-50 max-w-md mx-auto border-green-500 bg-green-50 text-green-900 shadow-lg">
        <AlertCircle className="h-4 w-4 text-green-500" />
        <AlertTitle>Back online</AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <span>Your connection has been restored.</span>
          <Button 
            size="sm" 
            variant="outline" 
            className="border-green-500 hover:bg-green-100 text-green-700"
            onClick={() => setWasOffline(false)}
          >
            Dismiss
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Show offline message
  return (
    <Alert className="fixed bottom-4 left-4 right-4 z-50 max-w-md mx-auto border-amber-500 bg-amber-50 text-amber-900 shadow-lg">
      <WifiOff className="h-4 w-4 text-amber-500" />
      <AlertTitle>You're offline</AlertTitle>
      <AlertDescription className="flex flex-col gap-2">
        <p>
          TradesPurple is running in offline mode. Some features may be limited until your connection is restored.
        </p>
        <div className="flex justify-between items-center mt-2">
          {isOnline ? (
            <Button 
              size="sm" 
              variant="outline" 
              className="border-amber-500 hover:bg-amber-100 text-amber-700"
              onClick={() => setShowAlert(false)}
            >
              Dismiss
            </Button>
          ) : (
            <Button 
              size="sm"
              variant="outline"
              className="border-amber-500 hover:bg-amber-100 text-amber-700 flex items-center gap-1"
              onClick={handleRefresh}
            >
              <RefreshCw className="h-3 w-3" /> Refresh
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
}

export default OfflineNotice;